import { Box, Button, Divider, Grid, Stack, Typography, styled, useTheme } from '@mui/material'
import { FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Chip } from 'src/components/common/Chip'
import { DynamicTextTypography } from 'src/components/DynamicTextTypography'
import { DeviationStatus } from 'src/enums'
import { IDeviation } from 'src/generated-types/deviation'
import { IDeviationKeywords } from 'src/generated-types/deviation-keywords'
import { IDeviationMeasure } from 'src/generated-types/deviation-measure'
import { IDeviationPriority } from 'src/generated-types/deviation-priority'
import { MUIThemeColors } from 'src/mui-theme'
import { Flex } from 'src/styles/flexComponent'
import { getFormattedDate, getThemeColor } from 'src/utils'
import { DeviationMap } from './DeviationMap'

const DESCRIPTION_CLAMP_LINES = 3

export const DeviationDetailSection: FC<{ deviation: IDeviation }> = ({ deviation }) => {
  const { t } = useTranslation(['deviation', 'common'])
  const theme = useTheme()
  const [descriptionExpanded, setDescriptionExpanded] = useState(false)
  const deviationType = useMemo(() => {
    const deviationType: string[] = []
    if (deviation?.visualDeviation) deviationType.push('visualDeviation')
    if (deviation?.abnormalWear) deviationType.push('abnormalWear')
    if (deviation?.measuredDeviation) deviationType.push('measuredDeviation')
    if (deviation?.normalWear) deviationType.push('normalWear')
    return deviationType
  }, [deviation?.visualDeviation, deviation?.abnormalWear, deviation?.measuredDeviation, deviation?.normalWear])

  const mapPosition = useMemo(() => {
    const pos = deviation?.positions?.[0]
    if (!pos) return null
    return { latitude: pos.latitude, longitude: pos.longitude }
  }, [deviation?.positions])

  const completedDate = deviation?.deviationFollowUps?.find(df => df.status)?.createdAt
  const hasKeywords = deviation?.keywordsArray && deviation?.keywordsArray.length > 0
  const hasType = deviationType.length > 0
  const hasMeasures = !!(deviation?.measures)
  const hasReason = !!(deviation?.reason)
  const hasPriority = deviation?.priority != null
  // Guard measure strictly: must be a non-null number with a known enum name
  const hasMeasure =
    deviation?.measure != null &&
    IDeviationMeasure[deviation.measure as IDeviationMeasure] !== undefined
  const hasResponsible = !!(deviation?.responsiblePersonName)
  const hasDueDate = !!(deviation?.dueDate)
  const hasCreatedAt = !!(deviation?.createdAt)
  const hasDates = hasDueDate || hasCreatedAt || !!completedDate

  return (
    <Box sx={{ px: 1, pt: 0.5, pb: 0.5 }}>
      {/* Two-column layout: main content left (9/12), dates stacked right (3/12) */}
      <Grid container spacing={0} columnSpacing={2} rowSpacing={0.5}>
        {/* LEFT: main content column */}
        <Grid item xs={12} md={hasDates ? 9 : 12}>
          <Grid container spacing={0} columnSpacing={2} rowSpacing={0.5}>
            {/* Row 1: Status / Ansvarlig / Prioritet / Forebygging (measure) */}
            {/* Status: only show when not closed — the "Lukket av" chip in the header already conveys closed state */}
            {!deviation?.status && (
              <Grid item xs={6} md={3} sx={{ py: 0.25 }}>
                <Flex.Column data-cy="status">
                  <TitleTypography variant="body2">{t('status', { ns: 'common' })}</TitleTypography>
                  {deviation?.deviationStatus === DeviationStatus.InApproval ? (
                    <Box>
                      <Chip
                        label={t('pendingApproval', { ns: 'deviation' })}
                        size="small"
                        sx={{
                          backgroundColor: getThemeColor(theme, MUIThemeColors.warningLight),
                          color: getThemeColor(theme, MUIThemeColors.white),
                        }}
                      />
                      {deviation?.serviceStationName && (
                        <ContentTypography variant="body2" sx={{ mt: 0.25 }}>
                          {deviation.serviceStationName}
                        </ContentTypography>
                      )}
                    </Box>
                  ) : (
                    <ContentTypography variant="body2">{t('open', { ns: 'common' })}</ContentTypography>
                  )}
                </Flex.Column>
              </Grid>
            )}
            {hasResponsible && (
              <Grid item xs={6} md={3} sx={{ py: 0.25 }}>
                <Flex.Column data-cy="responsiblePerson">
                  <TitleTypography variant="body2">{t('responsiblePerson')}</TitleTypography>
                  <ContentTypography variant="body2">{deviation.responsiblePersonName}</ContentTypography>
                </Flex.Column>
              </Grid>
            )}
            {hasPriority && (
              <Grid item xs={6} md={3} sx={{ py: 0.25 }}>
                <Flex.Column data-cy="priority">
                  <TitleTypography variant="body2">{t('priority', { ns: 'common' })}</TitleTypography>
                  <ContentTypography variant="body2">
                    {t(IDeviationPriority[deviation.priority as IDeviationPriority])}
                  </ContentTypography>
                </Flex.Column>
              </Grid>
            )}
            {hasMeasure && (
              <Grid item xs={6} md={3} sx={{ py: 0.25 }}>
                <Flex.Column data-cy="measure">
                  <TitleTypography variant="body2">{t('measure')}</TitleTypography>
                  <ContentTypography variant="body2">
                    {t(IDeviationMeasure[deviation.measure as IDeviationMeasure])}
                  </ContentTypography>
                </Flex.Column>
              </Grid>
            )}

            {/* Row 2: Keywords / Type — skip empty */}
            {hasKeywords && (
              <Grid item xs={12} md={3} sx={{ py: 0.25 }}>
                <Flex.Column>
                  <TitleTypography variant="body2">{t('keywords', { ns: 'common' })}</TitleTypography>
                  <Flex.Row display={'flex'} flexWrap={'wrap'} gap={0.5}>
                    {deviation.keywordsArray!.map((keyword, index) => (
                      <StyledChip variant="outlined" key={index} label={t(IDeviationKeywords[keyword])} size="small" />
                    ))}
                  </Flex.Row>
                </Flex.Column>
              </Grid>
            )}
            {hasType && (
              <Grid item xs={12} md={3} sx={{ py: 0.25 }}>
                <Flex.Column>
                  <TitleTypography variant="body2">{t('type', { ns: 'common' })}</TitleTypography>
                  <Flex.Row display={'flex'} flexWrap={'wrap'} gap={0.5}>
                    {deviationType.map((keyword, index) => (
                      <StyledChip variant="outlined" key={index} label={t(keyword)} size="small" />
                    ))}
                  </Flex.Row>
                </Flex.Column>
              </Grid>
            )}

            {/* Row 3: Tiltak (measures text) | Grunnen til (reason) — always on their own row, side by side */}
            {(hasMeasures || hasReason) && (
              <Grid item xs={12} sx={{ py: 0.25 }}>
                <Grid container spacing={0} columnSpacing={2}>
                  {hasMeasures && (
                    <Grid item xs={12} md={6}>
                      <Flex.Column data-cy="measures">
                        <TitleTypography variant="body2">{t('measures')}</TitleTypography>
                        <ContentTypography variant="body2">{deviation.measures}</ContentTypography>
                      </Flex.Column>
                    </Grid>
                  )}
                  {hasReason && (
                    <Grid item xs={12} md={6}>
                      <Flex.Column data-cy="reason">
                        <TitleTypography variant="body2">{t('reason')}</TitleTypography>
                        <ContentTypography variant="body2">{deviation.reason}</ContentTypography>
                      </Flex.Column>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            )}

            {/* Beskrivelse — full width with clamp; skip if empty */}
            {deviation?.description && (
              <Grid item xs={12} sx={{ py: 0.25, mt: 0.5 }}>
                <Divider sx={{ mb: 0.75 }} />
                <Flex.Column>
                  <TitleTypography variant="body2">{t('description', { ns: 'common' })}</TitleTypography>
                  <ContentTypography
                    variant="body2"
                    sx={
                      deviation.description.length > 200 && !descriptionExpanded
                        ? {
                            display: '-webkit-box',
                            WebkitLineClamp: DESCRIPTION_CLAMP_LINES,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            whiteSpace: 'pre-wrap',
                          }
                        : { whiteSpace: 'pre-wrap' }
                    }
                  >
                    {deviation.description}
                  </ContentTypography>
                  {deviation.description.length > 200 && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => setDescriptionExpanded(prev => !prev)}
                      sx={{ alignSelf: 'flex-start', p: 0, minWidth: 0, textTransform: 'none', mt: 0.25 }}
                    >
                      {descriptionExpanded ? t('showLess', { ns: 'common' }) : t('showMore', { ns: 'common' })}
                    </Button>
                  )}
                </Flex.Column>
              </Grid>
            )}
          </Grid>
        </Grid>

        {/* RIGHT: dates stacked vertically — only render when at least one date exists */}
        {hasDates && (
          <Grid item xs={12} md={3} sx={{ pt: { xs: 0.5, md: 0.25 } }}>
            <Stack spacing={1}>
              {hasCreatedAt && (
                <Flex.Column data-cy="createdAt">
                  <TitleTypography variant="body2">{t('createdAt', { ns: 'common' })}</TitleTypography>
                  <ContentTypography variant="body2">{getFormattedDate(deviation.createdAt)}</ContentTypography>
                </Flex.Column>
              )}
              {hasDueDate && (
                <Flex.Column data-cy="dueDate">
                  <TitleTypography variant="body2">{t('dueDate', { ns: 'common' })}</TitleTypography>
                  <ContentTypography variant="body2">{getFormattedDate(deviation.dueDate)}</ContentTypography>
                </Flex.Column>
              )}
              {completedDate && (
                <Flex.Column data-cy="completedDate">
                  <TitleTypography variant="body2">{t('completedDate', { ns: 'deviation' })}</TitleTypography>
                  <ContentTypography variant="body2">{getFormattedDate(completedDate)}</ContentTypography>
                </Flex.Column>
              )}
            </Stack>
          </Grid>
        )}
      </Grid>
      {mapPosition && (
        <Box sx={{ mt: 1 }}>
          <DeviationMap latitude={mapPosition.latitude} longitude={mapPosition.longitude} />
        </Box>
      )}
    </Box>
  )
}

//styled components

export const TitleTypography = styled(Typography)(({ theme }) => ({
  '&&': {
    color: theme.palette.text.secondary,
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: 1.3,
    display: 'block',
    marginBottom: 0,
  },
}))

export const ContentTypography = styled(Typography)(({ theme, color }) => ({
  '&&': {
    color: color ? (color as string) : theme.palette.primaryMain[theme.palette.mode],
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: 1.4,
  },
}))

export const DescriptionTypography = styled(DynamicTextTypography)(({ theme, color }) => ({
  color: color ? (color as string) : theme.palette.primaryMain[theme.palette.mode],
  fontWeight: 500,
}))

export const StyledChip = styled(Chip)({
  borderRadius: 5,
})
